import { useState, useEffect } from 'react';
import UnassignedEvents from './UnassignedEvents';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { LogOut, Plus, Settings as SettingsIcon, Pencil, Trash2, Calendar as CalendarIcon, Building2 } from 'lucide-react';
import EventForm from './EventForm';
import SettingsForm from './SettingsForm';
import RoomsManager from './RoomsManager';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [events, setEvents] = useState([]);
  const [unassignedEvents, setUnassignedEvents] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showRoomsManager, setShowRoomsManager] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [settings, setSettings] = useState({ upcoming_events_count: 1 });
  const navigate = useNavigate();

  useEffect(() => {
    verifyAuth();
    fetchEvents();
    fetchUnassignedEvents();
    fetchSettings();
    fetchRooms();
  }, []);

  const verifyAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin');
      return;
    }

    try {
      await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      localStorage.removeItem('token');
      navigate('/admin');
      toast.error('Sesja wygasła. Zaloguj się ponownie.');
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      // Sort by date and time
      const sorted = response.data.sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return a.start_time.localeCompare(b.start_time);
      });
      setEvents(sorted);
    } catch (error) {
      toast.error('Błąd podczas pobierania imprez');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnassignedEvents = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/unassigned-events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnassignedEvents(response.data);
    } catch (error) {
      console.error('Error fetching unassigned events:', error);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API}/rooms`);
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/admin');
    toast.success('Wylogowano pomyślnie');
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę imprezę?')) return;

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Impreza usunięta pomyślnie');
      fetchEvents();
    } catch (error) {
      toast.error('Błąd podczas usuwania imprezy');
    }
  };

  const handleEventSaved = () => {
    setShowEventForm(false);
    setEditingEvent(null);
    fetchEvents();
  };

  const handleRefresh = () => {
    fetchEvents();
    fetchUnassignedEvents();
  };

  const handleSettingsSaved = () => {
    setShowSettings(false);
    fetchSettings();
  };

  const handleRoomsSaved = () => {
    setShowRoomsManager(false);
    fetchRooms();
    fetchEvents();
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return format(date, 'd MMMM yyyy', { locale: pl });
    } catch {
      return dateStr;
    }
  };

  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room ? room.name : 'Nieznana sala';
  };

  // Filter future events
  const today = new Date().toISOString().split('T')[0];
  const futureEvents = events.filter(event => event.date >= today);

  return (
    <div className="min-h-screen relative overflow-hidden"
         style={{
           background: 'linear-gradient(135deg, #f5f3ff 0%, #faf5ff 100%)'
         }}>

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Fredoka, sans-serif', color: '#6E35C0' }}>
              Panel Admina
            </h1>
            <p className="text-gray-600">Zarządzanie imprezami urodzinowymi</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowRoomsManager(true)}
              variant="outline"
              className="flex items-center gap-2 border-purple-300 hover:bg-purple-50"
              data-testid="rooms-button"
            >
              <Building2 className="w-4 h-4" />
              Sale
            </Button>
            <Button
              onClick={() => setShowSettings(true)}
              variant="outline"
              className="flex items-center gap-2 border-purple-300 hover:bg-purple-50"
              data-testid="settings-button"
            >
              <SettingsIcon className="w-4 h-4" />
              Ustawienia
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center gap-2 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              Wyloguj
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-white/80 backdrop-blur-sm border-purple-200 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Wszystkie imprezy</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold" style={{ color: '#6E35C0' }}>{futureEvents.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-white/80 backdrop-blur-sm border-orange-200 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Do przypisania</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold" style={{ color: '#f97316' }}>{unassignedEvents.length}</p>
            </CardContent>
          </Card>
          {rooms.map(room => (
            <Card key={room.id} className="bg-white/80 backdrop-blur-sm border-purple-200 shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">{room.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold" style={{ color: '#6E35C0' }}>
                  {futureEvents.filter(e => e.room_id === room.id).length}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Add Event Button */}
        <div className="mb-6">
          <Button
            onClick={() => {
              setEditingEvent(null);
              setShowEventForm(true);
            }}
            className="text-white font-semibold flex items-center gap-2 h-12 px-6"
            style={{ background: '#6E35C0' }}
            data-testid="add-event-button"
          >
            <Plus className="w-5 h-5" />
            Dodaj nową imprezę
          </Button>
        </div>

        {/* Unassigned Events Section */}
        {unassignedEvents.length > 0 && (
          <div className="mb-8">
            <UnassignedEvents
              unassignedEvents={unassignedEvents}
              rooms={rooms}
              onRefresh={handleRefresh}
            />
          </div>
        )}

        {/* Events List */}
        <Card className="bg-white/80 backdrop-blur-sm border-purple-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Zaplanowane imprezy
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-gray-500">Ładowanie...</p>
            ) : futureEvents.length === 0 ? (
              <p className="text-center py-8 text-gray-500">Brak zaplanowanych imprez</p>
            ) : (
              <div className="space-y-3">
                {futureEvents.map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg border border-purple-200 hover:shadow-md transition-shadow"
                    data-testid={`event-item-${event.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                          {event.name}
                        </h3>
                        <span className="px-3 py-1 rounded-full text-sm font-semibold"
                              style={{
                                background: '#e9d5ff',
                                color: '#6E35C0'
                              }}>
                          {getRoomName(event.room_id)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-4 h-4" />
                          {formatDate(event.date)}
                        </span>
                        <span className="font-semibold">
                          {event.start_time} - {event.end_time}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setEditingEvent(event);
                          setShowEventForm(true);
                        }}
                        variant="ghost"
                        size="sm"
                        className="hover:bg-blue-100"
                        data-testid={`edit-event-${event.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDeleteEvent(event.id)}
                        variant="ghost"
                        size="sm"
                        className="hover:bg-red-100 hover:text-red-600"
                        data-testid={`delete-event-${event.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Event Form Dialog */}
      {showEventForm && (
        <EventForm
          event={editingEvent}
          rooms={rooms}
          onClose={() => {
            setShowEventForm(false);
            setEditingEvent(null);
          }}
          onSave={handleEventSaved}
        />
      )}

      {/* Settings Dialog */}
      {showSettings && (
        <SettingsForm
          settings={settings}
          onClose={() => setShowSettings(false)}
          onSave={handleSettingsSaved}
        />
      )}

      {/* Rooms Manager Dialog */}
      {showRoomsManager && (
        <RoomsManager
          rooms={rooms}
          onClose={() => setShowRoomsManager(false)}
          onSave={handleRoomsSaved}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
